import React from 'react';

/**
 * Work-order filters.
 *
 * This file used to render its entire tab list twice — once for mobile, once
 * for desktop — with seven different button colours (purple / yellow / green /
 * indigo / orange / teal / blue) that carried no meaning. There is now a single
 * active treatment, counts as neutral badges, and two layouts over the same
 * seven filter ids: a grouped sidebar on desktop, two tap rows on mobile.
 */

const GROUPS = [
  {
    items: [{ id: 'all', label: 'All work', count: (c) => c.total }],
  },
  {
    legend: 'Preventive maintenance',
    items: [
      { id: 'all-ppm', label: 'All PPM', count: (c) => c.totalPPM },
      { id: 'ppm-active', label: 'Active', count: (c) => c.activePPM },
      { id: 'ppm-completed', label: 'Completed', count: (c) => c.completedPPM },
    ],
  },
  {
    legend: 'Service requests',
    items: [
      { id: 'all-sr', label: 'All SR', count: (c) => c.totalSR },
      { id: 'sr-active', label: 'Active', count: (c) => c.activeSR },
      { id: 'sr-completed', label: 'Completed', count: (c) => c.completedSR },
    ],
  },
];

/**
 * Mobile presentation of the same seven filters, split into two short rows so
 * nothing has to be swiped into view: pick the work type, then narrow by status.
 * The ids are unchanged — this is only a different way of choosing them.
 */
const TYPE_ROW = [
  { id: 'all', label: 'All work', count: (c) => c.total },
  { id: 'all-ppm', label: 'PPM', count: (c) => c.totalPPM },
  { id: 'all-sr', label: 'SR', count: (c) => c.totalSR },
];

const STATUS_ROWS = {
  'all-ppm': [
    { id: 'all-ppm', label: 'All', count: (c) => c.totalPPM },
    { id: 'ppm-active', label: 'Active', count: (c) => c.activePPM },
    { id: 'ppm-completed', label: 'Done', count: (c) => c.completedPPM },
  ],
  'all-sr': [
    { id: 'all-sr', label: 'All', count: (c) => c.totalSR },
    { id: 'sr-active', label: 'Active', count: (c) => c.activeSR },
    { id: 'sr-completed', label: 'Done', count: (c) => c.completedSR },
  ],
};

/** Which type row entry owns the current tab. */
const typeOf = (tab) => {
  if (tab.startsWith('ppm') || tab === 'all-ppm') return 'all-ppm';
  if (tab.startsWith('sr') || tab === 'all-sr') return 'all-sr';
  return 'all';
};

const Count = ({ value, active }) => (
  <span
    className={`tabular ml-auto rounded-sm px-1.5 py-0.5 text-xs font-medium ${
      active ? 'bg-white/20 text-current' : 'bg-muted text-muted-fg'
    }`}
  >
    {value}
  </span>
);

const MobileTab = ({ item, isActive, statusCounts, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(item.id)}
    aria-pressed={isActive}
    className={[
      'flex min-h-11 items-center justify-center gap-1.5 rounded-md border px-2 text-sm font-medium',
      'cursor-pointer transition-colors duration-fast ease-out',
      isActive
        ? 'border-accent bg-accent text-accent-fg'
        : 'border-border bg-surface text-foreground hover:bg-muted',
    ].join(' ')}
  >
    <span className="truncate">{item.label}</span>
    <Count value={item.count(statusCounts)} active={isActive} />
  </button>
);

const QDFFilterTabs = ({ activeTab, statusCounts, onChange }) => {
  const currentType = typeOf(activeTab);
  const statusRow = STATUS_ROWS[currentType];

  return (
  <>
    {/* Mobile: type on one row, status on the next. Everything visible at 375px,
        so no filter is hidden behind a horizontal swipe. */}
    <div className="mb-4 space-y-2 lg:hidden">
      <div className="grid grid-cols-3 gap-2">
        {TYPE_ROW.map((item) => (
          <MobileTab
            key={item.id}
            item={item}
            isActive={currentType === item.id}
            statusCounts={statusCounts}
            onChange={onChange}
          />
        ))}
      </div>

      {statusRow && (
        <div className="grid grid-cols-3 gap-2">
          {statusRow.map((item) => (
            <MobileTab
              key={item.id}
              item={item}
              isActive={activeTab === item.id}
              statusCounts={statusCounts}
              onChange={onChange}
            />
          ))}
        </div>
      )}
    </div>

    {/* Desktop: grouped sidebar. */}
    <aside className="hidden w-72 shrink-0 lg:block">
      <div className="rounded-lg border border-border bg-surface shadow-sm">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold text-foreground">Filters</h2>
        </div>

        <div className="space-y-5 p-3">
          {GROUPS.map((group, i) => (
            <div key={group.legend || i}>
              {group.legend && (
                <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-fg">
                  {group.legend}
                </h3>
              )}
              <div className="space-y-1">
                {group.items.map(({ id, label, count }) => {
                  const isActive = activeTab === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onChange(id)}
                      aria-pressed={isActive}
                      className={[
                        'flex min-h-11 w-full items-center gap-2 rounded-md px-3 text-left text-sm font-medium',
                        'cursor-pointer transition-colors duration-fast ease-out',
                        isActive
                          ? 'bg-accent text-accent-fg'
                          : 'text-foreground hover:bg-muted',
                      ].join(' ')}
                    >
                      {label}
                      <Count value={count(statusCounts)} active={isActive} />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  </>
  );
};

export default QDFFilterTabs;
